import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  Upload,
  FileCheck,
  PenLine,
  Newspaper,
  Search as SearchIcon,
  CalendarClock,
  Stethoscope,
  Building2,
  TrendingUp,
  BookOpen,
} from 'lucide-react';
import { initAnalytics, trackPageView } from './utils/analytics';
import { applyPageMeta } from './utils/pageMeta';
import { initMobileOAuthListener, setOAuthNavigate } from './utils/oauthMobile';
import { initNativeShell } from './utils/nativeApp';
import Footer from './components/Footer';
import RequireAuth from './components/RequireAuth';
import NotificationBell from './components/NotificationBell';
import { apiGet, apiPost } from './utils/api';
import './App.css';

// match: このメニューを「今見ているページ」として赤くするパスの接頭辞
const NAV_ITEMS = [
  { to: '/feed', label: 'フィード', short: 'フィード', Icon: Newspaper, match: ['/feed'] },
  { to: '/search', label: '検索', short: '検索', Icon: SearchIcon, match: ['/search'] },
  { to: '/timelines', label: '経過タイムライン', short: '経過', Icon: CalendarClock, match: ['/timelines', '/timeline/'] },
  { to: '/karte/new', label: '術後カルテを書く', short: 'カルテを書く', Icon: PenLine, match: ['/karte'] },
  { to: '/procedures', label: '施術まとめ', short: '施術', Icon: Stethoscope, match: ['/procedures', '/procedure/'] },
  { to: '/clinics', label: 'クリニック', short: 'クリニック', Icon: Building2, match: ['/clinics', '/clinic/'] },
  { to: '/statistics', label: 'いま話題', short: 'いま話題', Icon: TrendingUp, match: ['/statistics'] },
  { to: '/insights', label: 'まとめ記事', short: 'まとめ記事', Icon: BookOpen, match: ['/insights'] },
];
const KARTE_NAV_ITEM = NAV_ITEMS.find((item) => item.to === '/karte/new');
const DESKTOP_NAV_ITEMS = NAV_ITEMS.filter((item) => item !== KARTE_NAV_ITEM);

function isNavActive(item, pathname) {
  return item.match.some((prefix) => pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`));
}

const Home = lazy(() => import('./pages/Home'));
const Feed = lazy(() => import('./pages/Feed'));
const Search = lazy(() => import('./pages/Search'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const ProcedureSummary = lazy(() => import('./pages/ProcedureSummary'));
const ProceduresIndex = lazy(() => import('./pages/ProceduresIndex'));
const Timelines = lazy(() => import('./pages/Timelines'));
const TimelineDetail = lazy(() => import('./pages/TimelineDetail'));
const Clinics = lazy(() => import('./pages/Clinics'));
const ClinicDetail = lazy(() => import('./pages/ClinicDetail'));
const Stats = lazy(() => import('./pages/Stats'));
const InsightsIndex = lazy(() => import('./pages/InsightsIndex'));
const InsightsArticle = lazy(() => import('./pages/InsightsArticle'));
const GeminiFeatures = lazy(() => import('./components/GeminiFeatures'));
const AiMasterChat = lazy(() => import('./pages/AiMasterChat'));
const EvidenceSubmit = lazy(() => import('./pages/EvidenceSubmit'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminReview = lazy(() => import('./pages/admin/AdminReview'));
const AdminContentReview = lazy(() => import('./pages/admin/AdminContentReview'));
const AdminBatch = lazy(() => import('./pages/admin/AdminBatch'));
const AdminStats = lazy(() => import('./pages/admin/AdminStats'));
const AdminTwitter = lazy(() => import('./pages/admin/AdminTwitter'));
const AdminData = lazy(() => import('./pages/admin/AdminData'));
const AdminKeywords = lazy(() => import('./pages/admin/AdminKeywords'));
const AdminTweetImport = lazy(() => import('./pages/admin/AdminTweetImport'));
const AdminBackfill = lazy(() => import('./pages/admin/AdminBackfill'));
const AdminSearchLog = lazy(() => import('./pages/admin/AdminSearchLog'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const MyEvidence = lazy(() => import('./pages/MyEvidence'));
const Login = lazy(() => import('./pages/Login'));
const AuthSuccess = lazy(() => import('./pages/AuthSuccess'));
const AuthError = lazy(() => import('./pages/AuthError'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const MyPage = lazy(() => import('./pages/MyPage'));
const TweetImportUrl = lazy(() => import('./pages/TweetImportUrl'));
const MyPostsImportRequest = lazy(() => import('./pages/MyPostsImportRequest'));
const EarnLanding = lazy(() => import('./pages/EarnLanding'));
const KarteCreate = lazy(() => import('./pages/KarteCreate'));
const KarteView = lazy(() => import('./pages/KarteView'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const AdminInquiries = lazy(() => import('./pages/admin/AdminInquiries'));
const AdminEarnings = lazy(() => import('./pages/admin/AdminEarnings'));
const AdminKeywordSuggestions = lazy(() => import('./pages/admin/AdminKeywordSuggestions'));
const AdminPostDelete = lazy(() => import('./pages/admin/AdminPostDelete'));
const AdminComments = lazy(() => import('./pages/admin/AdminComments'));
const AdminModeration = lazy(() => import('./pages/admin/AdminModeration'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminKartes = lazy(() => import('./pages/admin/AdminKartes'));
const AdminClinicCrawl = lazy(() => import('./pages/admin/AdminClinicCrawl'));

function readStoredUser() {
  try {
    const saved = localStorage.getItem('user');
    if (!saved) return null;
    return JSON.parse(saved);
  } catch {
    localStorage.removeItem('user');
    return null;
  }
}

const initialStoredUser = readStoredUser();

function RouteProgressFallback() {
  return (
    <div
      className="min-h-[40vh] flex items-center justify-center"
      aria-busy="true"
      aria-label="読み込み中"
    >
      <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const page = location.pathname + location.search;
    applyPageMeta(location.pathname, location.search);
    trackPageView(page);
  }, [location]);
  return null;
}

function NativeBootstrap() {
  const navigate = useNavigate();

  useEffect(() => {
    setOAuthNavigate(navigate);
    return initMobileOAuthListener();
  }, [navigate]);

  useEffect(() => {
    return initNativeShell({ navigate });
  }, [navigate]);

  return null;
}

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAuthRoute = /^\/(login|auth\/)/.test(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [user, setUser] = useState(() => initialStoredUser);
  // キャッシュユーザーがあっても、RequireAuth ページは /api/auth/me の結果を待つ
  // （無効トークンのまま /chat 等が開くのを防ぐ）
  const [loadingUser, setLoadingUser] = useState(true);
  const userMenuButtonRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const checkUser = async () => {
      setLoadingUser(true);
      const parsedUser = readStoredUser();
      setUser(parsedUser);
      // Cookie セッションを含めてサーバー側のログイン状態を最終確認
      await verifyToken(parsedUser);
      setLoadingUser(false);
    };

    // 初回チェック
    checkUser();

    // localStorageの変更を監視（他のタブやウィンドウでの変更も検知）
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token' || e.key === 'user') {
        checkUser();
      }
    };

    // カスタムイベントでログイン状態の変更を検知
    const handleAuthChange = () => {
      checkUser();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authStateChanged', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  const verifyToken = async (fallbackUser = null) => {
    try {
      const userData = await apiGet('/api/auth/me');
      if (userData?.user) {
        setUser(userData.user);
        localStorage.setItem('user', JSON.stringify(userData.user));
      } else {
        // OAuth 完了直後は AuthSuccess が hash/query の JWT を確定する前に
        // ここが走るとトークンを消して user_info_failed になる（2026-11-04 本番障害）。
        const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
        const onAuthCallback =
          path.startsWith('/auth/success') || path.startsWith('/auth/twitter');
        if (onAuthCallback) {
          return;
        }
        // 無効・期限切れトークンを残すと /chat 等が「ログイン済み風」に見える
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } catch (error) {
      // 一時的なネットワーク失敗で誤ってログアウト表示にしない
      const isUnauthorized = typeof error?.message === 'string' && error.message.includes('401');
      if (isUnauthorized) {
        const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
        if (path.startsWith('/auth/success') || path.startsWith('/auth/twitter')) {
          return;
        }
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
        return;
      }
      if (fallbackUser) {
        setUser(fallbackUser);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await apiPost('/api/auth/logout', {});
    } catch (error) {
      console.error('Error logging out:', error);
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // ユーザーメニュー: 開いたときにフォーカスをメニュー内へ、Escで閉じてボタンにフォーカス戻す
  useEffect(() => {
    if (!userMenuOpen) return;
    const firstLink = userMenuRef.current?.querySelector('a, button');
    if (firstLink) firstLink.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false);
        userMenuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [userMenuOpen]);

  // 認証チェック(/api/auth/me)はバックグラウンドで行い、画面は即描画する。
  // バックエンド(Cloud Run)がコールドスタート中でもトップページをブロックしない。
  // ログイン必須ページは RequireAuth 側が loadingUser を見て待つ。
  return (
    <>
      <PageTracker />
      <NativeBootstrap />
      <div className="app-shell min-h-screen flex flex-col bg-gray-50 w-full max-w-full" data-build={process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || process.env.NODE_ENV}>
        <a href="#main" className="skip-link">
          メインコンテンツへスキップ
        </a>
        {/* ナビゲーション - 管理画面は AdminLayout 側のヘッダーのみ表示 */}
        {!isAdminRoute && (
        <nav className="app-header bg-white/90 backdrop-blur border-b border-rose-100">
          <div className="app-header-inner max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full min-w-0">
            <div className="app-header-row flex justify-between items-center h-14 sm:h-16 gap-2 min-w-0">
              <div className="app-header-brand flex items-center">
                <Link
                  to="/"
                  className="app-header-brand-link font-rounded text-lg sm:text-xl font-black text-rose-500 block"
                  title="ウラカルテ"
                >
                  ウラカルテ
                </Link>
              </div>

              {/* デスクトップメニュー（注釈はログイン欄内に収め、ヘッダー高さ固定との干渉を避ける） */}
              <div className="app-header-desktop hidden xl:flex items-center">
                <div className="flex items-center flex-nowrap justify-end gap-x-0.5 lg:gap-x-1">
                {DESKTOP_NAV_ITEMS.map((item) => {
                  const active = isNavActive(item, location.pathname);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={`px-2.5 lg:px-3 py-2 rounded-full text-sm transition-colors whitespace-nowrap hover:text-rose-500 hover:bg-rose-50 ${
                        active ? 'text-rose-500 bg-rose-50 font-semibold' : 'text-gray-600 font-medium'
                      }`}
                    >
                      {item.short}
                    </Link>
                  );
                })}
                {(() => {
                  const active = isNavActive(KARTE_NAV_ITEM, location.pathname);
                  return (
                    <Link
                      to={KARTE_NAV_ITEM.to}
                      aria-current={active ? 'page' : undefined}
                      className={`ml-1 inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors hover:text-rose-500 hover:bg-rose-50 whitespace-nowrap ${
                        active
                          ? 'border-rose-300 bg-rose-50 text-rose-500 font-semibold'
                          : 'border-gray-200 bg-white text-gray-600 font-medium'
                      }`}
                    >
                      <PenLine className="h-4 w-4" aria-hidden />
                      {KARTE_NAV_ITEM.short}
                    </Link>
                  );
                })()}
                {user && <NotificationBell user={user} />}
                {user ? (
                  <div className="relative ml-1">
                    <button
                      ref={userMenuButtonRef}
                      type="button"
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center space-x-2 text-gray-600 hover:text-rose-500 hover:bg-rose-50 px-3 py-2 rounded-full text-sm font-medium transition-colors"
                      aria-expanded={userMenuOpen}
                      aria-haspopup="true"
                      aria-label="マイページメニュー"
                    >
                      {user.profile_image_url ? (
                        <img 
                          src={user.profile_image_url} 
                          alt={user.name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <User className="w-5 h-5" />
                      )}
                      <span>マイページ</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* ドロップダウンメニュー */}
                    {userMenuOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setUserMenuOpen(false)}
                          aria-hidden="true"
                        />
                        <div
                          ref={userMenuRef}
                          role="menu"
                          className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-lg shadow-rose-100/60 border border-rose-100 z-20"
                          aria-label="マイページメニュー"
                        >
                          <div className="py-1">
                            <Link
                              to="/mypage"
                              role="menuitem"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              <span>ダッシュボード</span>
                            </Link>
                            <Link
                              to="/evidence-submit"
                              role="menuitem"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            >
                              <Upload className="w-4 h-4" />
                              <span>エビデンス提出</span>
                            </Link>
                            <Link
                              to="/my-evidence"
                              role="menuitem"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            >
                              <FileCheck className="w-4 h-4" />
                              <span>提出状況</span>
                            </Link>
                            <div className="border-t border-rose-100 my-1" />
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                handleLogout();
                                setUserMenuOpen(false);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-colors text-left"
                            >
                              <LogOut className="w-4 h-4" />
                              <span>ログアウト</span>
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end shrink-0 max-w-[min(100%,14rem)] lg:max-w-xs ml-1">
                    <Link
                      to="/login"
                      className="inline-flex items-center space-x-2 bg-rose-500 text-white hover:bg-rose-600 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                      aria-label="ログイン"
                    >
                      <User className="w-4 h-4 shrink-0" />
                      <span>ログイン</span>
                    </Link>
                    <p className="text-[10px] lg:text-[11px] text-gray-400 text-right leading-snug px-1 mt-1 hidden lg:block">
                      取り込み・マイページ・エビデンスはログイン後
                    </p>
                  </div>
                )}
                </div>
              </div>

              {/* モバイルメニューボタン */}
              <div className="app-header-mobile-btn xl:hidden flex items-center flex-shrink-0 gap-1">
                {user && <NotificationBell user={user} />}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-600 hover:text-rose-500 hover:bg-rose-50 p-2 rounded-full transition-colors"
                  aria-label="メニュー"
                >
                  {mobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* モバイルメニュー */}
          {mobileMenuOpen && (
            <div className="xl:hidden border-t border-rose-100">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {!user && (
                  <p className="mx-1 mb-2 rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700 leading-relaxed">
                    フィード・検索はそのまま利用できます。URL取り込み・マイページ・エビデンスはログイン後です。
                  </p>
                )}
                {NAV_ITEMS.map((item) => {
                  const { to, label, Icon } = item;
                  const active = isNavActive(item, location.pathname);
                  return (
                    <Link
                      key={to}
                      to={to}
                      aria-current={active ? 'page' : undefined}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-base transition-colors hover:bg-rose-50 hover:text-rose-500 ${
                        active ? 'text-rose-500 bg-rose-50 font-semibold' : 'text-gray-700 font-medium'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" aria-hidden />
                      <span>{label}</span>
                    </Link>
                  );
                })}
                {user ? (
                  <>
                    <div className="border-t border-rose-100 mt-2 pt-2">
                      <div className="px-3 py-2 flex items-center space-x-2">
                        {user.profile_image_url ? (
                          <img
                            src={user.profile_image_url}
                            alt={user.name}
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <User className="w-5 h-5 text-gray-600" />
                        )}
                        <span className="text-sm text-gray-700 font-medium">{user.name}</span>
                      </div>
                    </div>
                    <Link
                      to="/mypage"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl text-base font-medium transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <LayoutDashboard className="w-4 h-4" />
                        <span>ダッシュボード</span>
                      </div>
                    </Link>
                    <Link
                      to="/evidence-submit"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl text-base font-medium transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <Upload className="w-4 h-4" />
                        <span>エビデンス提出</span>
                      </div>
                    </Link>
                    <Link
                      to="/my-evidence"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-3 py-2 text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl text-base font-medium transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <FileCheck className="w-4 h-4" />
                        <span>提出状況</span>
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl text-base font-medium transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <LogOut className="w-4 h-4" />
                        <span>ログアウト</span>
                      </div>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl text-base font-medium transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>ログイン</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}
        </nav>
        )}

        {/* メインコンテンツ */}
        <main
          id="main"
          className={`app-main flex-1 w-full max-w-7xl mx-auto min-w-0 overflow-x-hidden ${
            isAdminRoute
              ? 'px-0 sm:px-2 py-2 sm:py-3 lg:px-4 lg:py-4 admin-route-main'
              : isAuthRoute
                ? 'px-4 sm:px-6 lg:px-8 py-0 flex flex-col auth-route-main'
                : 'px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8'
          }`}
        >
          <Suspense fallback={<RouteProgressFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/search" element={<Search />} />
            <Route path="/post/:id" element={<PostDetail />} />
            <Route path="/timelines" element={<Timelines />} />
            <Route path="/timeline/:author" element={<TimelineDetail />} />
            <Route path="/procedures" element={<ProceduresIndex />} />
            <Route path="/procedure/:name" element={<ProcedureSummary />} />
            <Route path="/clinics" element={<Clinics />} />
            <Route path="/clinic/:name" element={<ClinicDetail />} />
            <Route path="/statistics" element={<Stats />} />
            <Route path="/insights" element={<InsightsIndex />} />
            <Route path="/insights/:slug" element={<InsightsArticle />} />
            <Route path="/ai-analysis" element={<GeminiFeatures />} />
            <Route path="/chat" element={<RequireAuth user={user} loadingUser={loadingUser}><AiMasterChat /></RequireAuth>} />
            <Route path="/evidence-submit" element={<RequireAuth user={user} loadingUser={loadingUser}><EvidenceSubmit /></RequireAuth>} />
            <Route path="/my-evidence" element={<RequireAuth user={user} loadingUser={loadingUser}><MyEvidence /></RequireAuth>} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="review" element={<AdminReview />} />
              <Route path="content-review" element={<AdminContentReview />} />
              <Route path="batch" element={<AdminBatch />} />
              <Route path="stats" element={<AdminStats />} />
              <Route path="earnings" element={<AdminEarnings />} />
              <Route path="twitter" element={<AdminTwitter />} />
              <Route path="keywords" element={<AdminKeywords />} />
              <Route path="clinic-crawl" element={<AdminClinicCrawl />} />
              <Route path="tweet-import" element={<AdminTweetImport />} />
              <Route path="backfill" element={<AdminBackfill />} />
              <Route path="keyword-suggestions" element={<AdminKeywordSuggestions />} />
              <Route path="search-log" element={<AdminSearchLog />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="data" element={<AdminData />} />
              <Route path="post-delete" element={<AdminPostDelete />} />
              <Route path="comments" element={<AdminComments />} />
              <Route path="moderation" element={<AdminModeration />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="kartes" element={<AdminKartes />} />
            </Route>
            <Route path="/mypage" element={<RequireAuth user={user} loadingUser={loadingUser}><MyPage /></RequireAuth>} />
            <Route path="/earn" element={<EarnLanding />} />
            <Route path="/karte/new" element={<KarteCreate />} />
            <Route path="/karte/:id" element={<KarteView />} />
            <Route path="/import/url" element={<RequireAuth user={user} loadingUser={loadingUser}><TweetImportUrl /></RequireAuth>} />
            <Route path="/import/my-posts" element={<RequireAuth user={user} loadingUser={loadingUser}><MyPostsImportRequest /></RequireAuth>} />
            <Route path="/user/:identifier" element={<UserProfilePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/auth/twitter/callback" element={<AuthSuccess />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    </>
  );
}

export default App; 